import { Injectable } from '@angular/core';
import { Router, Resolve, RouterStateSnapshot, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of, EMPTY, forkJoin } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { ApiService } from '../core/api.service';
import { Lesson } from '../core/models/lesson';
import { SystemService } from '../core/system.service';


@Injectable({
  providedIn: 'root'
})
export class KeylessonResolverService implements Resolve<Lesson> {
  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private systemService: SystemService,
    private router: Router) { } 

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<Lesson> | Observable<never> {
    const id = +route.paramMap.get('id');
    
    const { userid, language_in_use } = this.authService.user$.value;
    if(!this.systemService.inGameMode$.value) { 
      this.router.navigate(['keylesson']);
      return EMPTY;
    }


    const requests = [
      this.apiService.loadBookmarksForLesson(userid, id, language_in_use),
      this.apiService.loadTargetLetters(userid, id, language_in_use),
      //this.apiService.loadLessonsExercises(userid, id),
      this.apiService.loadBestExercise(userid, id, language_in_use),
      //this.apiService.loadExerciseVideos(userid, id),
      this.apiService.loadLessonsExercises(userid, id, language_in_use),
      this.apiService.loadExerciseVideos(userid, id, language_in_use)
    ];

    return forkJoin([...requests])
      .pipe(
        mergeMap((values:any[]) => {
        if(!values) { return EMPTY }

        let lesson = new Lesson();
        lesson.id = id;

        // bookmarks
        const bookmarkResult = values[0];
        const bookmarks: any[] = bookmarkResult.zz.z;
        const lastBookmark = this.processBookmarks(bookmarks);
        lesson.lastAccess = lastBookmark;

        // letters
        const lettersResult = values[1]
        const letters: string[] = lettersResult.letters.letter;
        lesson.letters = letters;

        // best exercise
        const bestExecResult = values[2]
        const bestExercises: any[] = bestExecResult.zz.z;
        const best = this.processBestExercise(bestExercises);
        lesson.bestExercise = best;

        // last exercise
        const lastExecResult = values[2]
        const lastExercises: any[] = lastExecResult.zz.z;
        const last = this.processLastExercise(lastExercises);
        lesson.lastExercise = last;


        // exercises list
        const exercisesResult = values[3]
        const exercises: string[] = exercisesResult.lesson.ex;
        lesson.exercises = exercises;

        // exercises list
        const videosResult = values[4]
        let videos: string[] = videosResult.lesson.ex;
        videos = videos.map((value: string) => value.replace('Flv', 'MP4'))
        videos = videos.map((value: string) => value.replace('flv', 'mp4'))

        videos.map((value, index) => lesson.videos.set(index + 1, value));

        return of(lesson);

        })
      );
  }


  private processBookmarks(bookmarks: string[]): string {
    // check first entry 
    const parts = bookmarks[0].split(' ');
    if(parts.length > 3) { return ''};

    // should return last bookmarked date
    return parts[0];

  }

  private processBestExercise(bestExercises: string[]): string {
    // check first entry 
    const parts = bestExercises[0].split(' ');
    if(parts.length > 3) { return ''};

    // should return max exercise done
    const numbers = bestExercises.map(value => +value).filter(value => !isNaN(value))
    return Math.max(...numbers).toString();

  }

  private processLastExercise(lastExercises: string[]): number {
    // check first entry 
    const parts = lastExercises[0].split(' ');
    if(parts.length > 3) { return 0};

    // should return max exercise done
    const numbers = lastExercises.map(value => +value).filter(value => !isNaN(value))

    return numbers.length > 0 ? numbers[0] : 1;
  }

}